/*
Description
    Thin API wrapper for Grower Syndicate Splits (Project 2116, Almondco Intake
    Masters Migration - #76547). Feature-scoped, matching the sibling
    almondco-grower-pool-split.service.js convention rather than folding into the
    general IntakeSettingService, since these calls are specific to this feature.

History
    31Aug2026 (SD)
        - #76547: 2116 - UI - Grower Syndicate Splits List and filter panel
        - #76577: 2116 - UI - Grower Syndicate Split detail panel (Review/Edit grid,
          running Total, self-reference check). Added getGrowerSplit/
          getRecipientGrowers/saveGrowerSplit - the detail grid's own read/save calls.
        - #76580: 2116 - UI - Add New Season+Syndicate picker (unsplit combinations).
          Added getIntakeSeasons (Season option list, current-FY resolution mirrors
          console-season-year.vue's own setDefaultSeasonYear), getUnsplitSyndicates
          (Syndicate picker's own filtered dataSource), and
          getSyndicateSplitSummaryByKey (resolves the fresh RecordId to route to
          after a Create save, since the create route has no :id to reuse).

(c) Copyright Graycorp Pty Ltd (ABN 12 082 807 761). All Rights Reserved.
*/
import ApiService from "../../api.service";
import appConfig from "../../../../app-config";

ApiService.init(appConfig.VUE_APP_ROOT_API);

const AlmondcoGrowerSyndicateSplitService = {
  // §5/AD-05: AlmondcoGrowerSyndicateSplitListView (real, saved splits only - NOT
  // the AD-03 candidate-cross-join AlmondcoGrowerSyndicateSplitSummary), filtered
  // to a single row's RecordId - resolves the identity row (Season/SyndicateName)
  // on baseView.id change, whether that's a row click or a direct/bookmarked URL.
  // RecordId uses the identical hash formula on both views (see
  // AlmondcoGrowerSyndicateSplitListView.sql), so it routes to the same record
  // either way - but only the ListView also covers a real split whose Season
  // predates AlmondcoIntakeSeason's own earliest row.
  async getSyndicateSplitSummary(recordId) {
    var url = `api/AlmondcoGrowerSyndicateSplitListView?$filter=RecordId eq ${recordId}`;
    return await ApiService.get(url);
  },

  // §5: api.AlmondcoGrowerSplit shim, filtered to the selected Syndicate+Season -
  // GrowerPoolCode is the shim's existing (Pool-named, naming friction flagged in
  // technical-design.md §8) alias for SUPP_CODE, which holds the Syndicate's own
  // code for this screen's rows. DepotDocket is always the fixed marker "0" - this
  // screen has no real Depot Docket concept at all.
  async getGrowerSplit(syndicateCode, seasonYear) {
    var url = `api/AlmondcoGrowerSplit?$filter=GrowerPoolCode eq '${syndicateCode}' and SeasonYear eq ${seasonYear} and DepotDocket eq '0'`;
    return await ApiService.get(url);
  },

  // §5/AD-01: reused, not rebuilt - Individual/Syndicate growers only, baked into
  // the view predicate. Loaded once (not per-row) - the Grower picker option list
  // is Syndicate+Season-independent.
  async getRecipientGrowers() {
    return await ApiService.get(
      "api/AlmondcoGrowerRecipientSearch?$orderby=GrowerName",
    );
  },

  // AD-02: payload always sends DepotDocket:"0" and this screen's own SuppCode
  // (the Syndicate's own code) explicitly - see AlmondcoGrowerSplitBusiness.Save.
  async saveGrowerSplit(payload) {
    return await ApiService.post("api/Save_AlmondcoGrowerSplit", payload);
  },

  // Season picker option list (Add-New, #76580). Season/StartDate/EndDate only -
  // "current FY" is resolved the same way console-season-year.vue's own
  // setDefaultSeasonYear does (find the row whose date range contains today),
  // reuse-before-create rather than a second definition of "current fiscal year".
  async getIntakeSeasons() {
    return await ApiService.get(
      "api/AlmondcoIntakeSeason?$select=Season,StartDate,EndDate&$orderby=Season desc",
    );
  },

  // Syndicate picker option list (Add-New, #76580) - unsplit (GrowerCount eq 0)
  // combinations for a specific Season, from Story 1's own summary endpoint.
  dataSourceConfig(url, params) {
    return ApiService.createDataSource(url, params);
  },

  // Resolves the fresh RecordId for a Syndicate+Season after a Create save - the
  // create route has no :id to reuse, and RecordId is a server-computed
  // deterministic hash (AlmondcoGrowerSyndicateSplitListView.sql), not something
  // to reproduce client-side. AD-05: queries the ListView, not the Summary view -
  // the save that just happened is exactly the "real split now exists" case this
  // view is sourced from.
  async getSyndicateSplitSummaryByKey(syndicateCode, seasonYear) {
    var url = `api/AlmondcoGrowerSyndicateSplitListView?$filter=SyndicateCode eq '${syndicateCode}' and SeasonYear eq ${seasonYear}`;
    return await ApiService.get(url);
  },
};

export { AlmondcoGrowerSyndicateSplitService };
